import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';
import { api } from '../api/api';
import { toast } from 'sonner';

const Offer2 = ({ userId }) => {
  const navigate = useNavigate();
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);

  const prices = { A: 40, B: 50 };

  useEffect(() => {
    const fetchVariant = async () => {
      try {
        const result = await api.getABVariant('offer2', userId);
        setVariant(result.variant);
      } catch (error) {
        console.error('Error fetching variant:', error);
        setVariant('A');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchVariant();
    }
  }, [userId]);

  const handleAccept = async () => {
    try {
      const price = prices[variant];
      await api.addToCart({
        userId,
        productId: 'digital-report-1',
        productName: 'Ultimate Uganda Travel Guide (Digital Report)',
        price,
        variant: variant
      });
      
      toast.success('✓ Travel Guide added to cart!');
      
      setTimeout(() => {
        navigate('/offer-3');
      }, 1000);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleDecline = async () => {
    try {
      await api.enrollInSequence({ userId, sequenceType: 'sequence2' });
      navigate('/offer-3');
    } catch (error) {
      console.error('Error enrolling in sequence:', error);
      navigate('/offer-3');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading offer...</p>
        </div>
      </div>
    );
  }

  const price = prices[variant];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-2 bg-gold-100 text-gold-800 rounded-full text-sm font-semibold mb-4">
              Special Offer #2
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{color: '#3D2817'}}>
              Ultimate Uganda Travel Guide
            </h2>
            <p className="text-xl text-gray-700">
              A comprehensive digital report (PDF)
            </p>
          </div>

          <div className="mb-8">
            <VideoPlayer videoUrl="" />
          </div>

          <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6" style={{color: '#C85A3E'}}>
              Inside This Comprehensive Guide:
            </h3>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>200+ pages</strong> of in-depth travel information</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Detailed itineraries</strong> for 7, 14, and 21-day trips</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Hidden gems</strong> and off-the-beaten-path destinations</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Budget planning tips</strong> for every type of traveler</p>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-gray-700"><strong>Safety and health information</strong> for worry-free travel</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-teal-50 rounded-lg p-6 text-center mb-8">
              <p className="text-gray-600 mb-2">Limited Time Offer</p>
              <div className="text-5xl font-bold mb-2" style={{color: '#8B3A9B'}}>
                ${price}
              </div>
              <p className="text-sm text-gray-600">One-time payment • Instant digital access</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleAccept}
                className="w-full py-4 rounded-lg font-bold text-white text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition"
                style={{backgroundColor: '#8B3A9B'}}
                data-testid="accept-offer-2"
              >
                Yes I want the report
              </button>
              
              <button
                onClick={handleDecline}
                className="w-full py-3 rounded-lg font-medium text-gray-600 border-2 border-gray-300 hover:border-gray-400 transition"
                data-testid="decline-offer-2"
              >
                No Thanks
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Offer2;